import type { Bairro } from "@/types/bairros";

export type IndicatorGroup = "Exemplares arbóreos" | "Resiliência territorial";

export interface Indicator {
  id: string;
  group: IndicatorGroup;
  label: string;
  /** Texto curto sob o título da legenda. */
  hint: string;
  /** Valor do bairro, ou null quando NÃO MEDIDO (nunca zero por falta de dado). */
  value: (b: Bairro) => number | null;
  format: (v: number) => string;
  /** high-good: mais é melhor (verde); high-bad: mais é pior (amarelo→vermelho). */
  direction: "high-good" | "high-bad";
  /** Aviso metodológico exibido junto ao indicador. */
  caveat?: string;
}

const int = (v: number) => Math.round(v).toLocaleString("pt-BR");
const pct = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const FITA_CAVEAT =
  "Estimativa visual preliminar (FITA) das quadras já analisadas. Serve para planejar e exige validação em campo.";

export const INDICATORS: Indicator[] = [
  {
    id: "trees",
    group: "Exemplares arbóreos",
    label: "Exemplares cadastrados",
    hint: "Árvores no inventário da sua organização",
    value: (b) => (b.treeCount > 0 ? b.treeCount : null),
    format: int,
    direction: "high-good",
  },
  {
    id: "risk",
    group: "Exemplares arbóreos",
    label: "Exemplares em risco alto ou crítico",
    hint: "Pela última vistoria validada",
    value: (b) => (b.treeCount > 0 ? b.highRiskCount : null),
    format: int,
    direction: "high-bad",
  },
  {
    id: "density",
    group: "Exemplares arbóreos",
    label: "Exemplares por 1.000 habitantes",
    hint: "Cadastrados em relação à população (Censo 2022)",
    value: (b) => (b.treeCount > 0 && b.population ? (b.treeCount / b.population) * 1000 : null),
    format: (v) => v.toLocaleString("pt-BR", { maximumFractionDigits: 1 }),
    direction: "high-good",
    caveat: "Mede o que está cadastrado, não o que existe na cidade: cresce conforme o inventário avança.",
  },
  {
    id: "canopy",
    group: "Resiliência territorial",
    label: "Cobertura de copa",
    hint: "Média das quadras analisadas",
    value: (b) => b.avgCanopyPercent,
    format: pct,
    direction: "high-good",
    caveat: FITA_CAVEAT,
  },
  {
    id: "impervious",
    group: "Resiliência territorial",
    label: "Impermeabilização",
    hint: "Média das quadras analisadas",
    value: (b) => b.avgImperviousPercent,
    format: pct,
    direction: "high-bad",
    caveat: FITA_CAVEAT,
  },
  {
    id: "heat",
    group: "Resiliência territorial",
    label: "Quadras com ilha de calor alta",
    hint: "Parcela das quadras analisadas em risco alto ou crítico",
    value: (b) => (b.analyzedBlocks > 0 ? (b.highHeatBlocks / b.analyzedBlocks) * 100 : null),
    format: pct,
    direction: "high-bad",
    caveat: FITA_CAVEAT,
  },
  {
    id: "planting",
    group: "Resiliência territorial",
    label: "Potencial de plantio",
    hint: "Vagas estimadas nas quadras analisadas",
    value: (b) => b.plantingPotentialCount,
    format: int,
    direction: "high-good",
    caveat: FITA_CAVEAT,
  },
];

const GOOD = ["#D7EBDD", "#A9D5B8", "#6DB78B", "#2F8F62", "#136A45"];
const BAD = ["#FFF1B8", "#FFD066", "#FFA43A", "#E4572E", "#9E1B1B"];

export interface Legend {
  /** Limites inferiores de cada classe, em ordem crescente. */
  breaks: number[];
  colors: string[];
}

/** Classes por intervalo igual entre o menor e o maior valor MEDIDO. */
export function buildLegend(values: number[], direction: Indicator["direction"]): Legend | null {
  if (values.length === 0) return null;
  const palette = direction === "high-good" ? GOOD : BAD;
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return { breaks: [min], colors: [palette[palette.length - 1]] };
  const step = (max - min) / palette.length;
  return { breaks: palette.map((_, i) => min + step * i), colors: palette };
}

export function colorFor(value: number, legend: Legend): string {
  let index = 0;
  for (let i = 0; i < legend.breaks.length; i += 1) {
    if (value >= legend.breaks[i]) index = i;
  }
  return legend.colors[index];
}
